// import { notifyBugsnag } from '@/utils/bugsnag'
import {
  get, post, put, destroy,
} from '@/utils/fetch'
import { setMxUserData } from '@/utils/mixpanel'
import { sessionStorageGet, sessionStorageSet } from '@/utils/storage'
import { getToken } from '@/utils/token'
import { setUserId } from '../cache/user'
import type { TProfessionalReviewPrivateInfo } from '@/interfaces/professional-review'
import type { TUserPrivateInfo, TUserPublicInfo, TUser } from '@/interfaces/user'
import type { AxiosRequestConfig } from 'axios'

type TUserMe = {
  user: TUserPrivateInfo
  professionalReview?: TProfessionalReviewPrivateInfo
}

type GetMeOptions = AxiosRequestConfig & {
  forceReload?: boolean
}

export const getMe = async (options?: GetMeOptions) => {
  if (!options?.forceReload && sessionStorageGet('auth')) {
    const auth = sessionStorageGet('auth')
    setUserId(auth.user.id)
    setMxUserData(auth.user)
    return auth as TUserMe
  }
  const token = getToken()
  if (!token && !options?.headers?.Authorization) {
    return null
  }
  const me = await get<TUserMe>('/user/me', options)
    .catch((error) => { error })
  if (!me || 'error' in me) {
    return me
  }

  if (!me?.user) {
    return null
  }
  setUserId(me.user.id)
  // setMxUserId(me.user.id)
  setMxUserData(me.user)

  sessionStorageSet('auth', me)

  return me
}

export const updateMe = async (updates: Partial<TUser>) => {
  const user = await put<TUserPrivateInfo>('/user/me', updates)
  if (!user || 'error' in user) {
    return user
  }
  if (sessionStorageGet('auth')) {
    const auth = sessionStorageGet('auth')
    auth.user = { ...auth.user, ...user }
    sessionStorageSet('auth', auth)
  }
  return user
}

export const removeMe = async () => {
  const user = await destroy<TUserPrivateInfo>('/user/me')
  return user
}

export const registerUser = async (
  id: string,
  name: string,
  username: string,
  userImage: string,
  token: string,
) => {
  const result = await post<TUserPrivateInfo & {token: string}>('/user', {
    _id: id,
    name,
    username,
    userImage,
    token,
  })
  return result
}

export const getUserPublicInfo = async ({
  username = '',
  email = '',
  id = '',
}: {
  username?: string
  email?: string
  id?: string
}) => {
  let query = ''
  if (username) {
    query = `?username=${username}`
  }
  if (email) {
    query = query
      ? `${query}&email=${email}`
      : `?email=${email}`
  }
  if (id) {
    query = query
      ? `${query}&id=${id}`
      : `?id=${id}`
  }
  const user = await get<TUserPublicInfo>(`/user${query}`)
  return user
}

export const getUserPublicInfoList = async (
  page = 1,
  perpage = 20,
) => {
  const userList = await get<TUserPublicInfo[]>(`/user/list?page=${page}&perpage=${perpage}`)
  return userList
}

type LatestUser = {
  username: string
  updatedAt: string
}

export const getAllUserPathInfoList = async () => {
  const userList = await get<LatestUser[]>('/user/list/path-info')
  return userList
}

export const getLatestUser = async () => {
  const userList = await get<LatestUser>('/user/latest')
  return userList
}

type RandomCreator = {
  username: string
  bio: string
}

export const getRandomCreators = async () => {
  const creators = await get<RandomCreator[]>('/user/list/random-creators')
  return creators
}

export type TTeamMemberInfo = {
  id: string
  username: string
  name?: string
  email: string
  userImage?: string | null
}

export const getMyTeamMembers = async (projectId: string) => {
  const result = await get<{ members: TTeamMemberInfo[] }>(`/user/me/team-members?projectId=${projectId}`)
  if (!result || 'error' in result) {
    return []
  }
  return result.members
}

export const getSoundReviewEligibility = async () => {
  const result = await get<{ firstTime: boolean }>('/user/me/sound-review-eligibility')
  if (!result || 'error' in result) {
    return null
  }
  return result
}

export const getAllUsernames = async () => {
  const result = await get<{ usernames: string[] }>('/user/list/all')
  if (!result || 'error' in result) {
    return []
  }

  return result.usernames
}
